import { Flex, Heading, Text, View } from "@instructure/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { StructuredQuestionService } from "../../../../services/StructuredQuestionService";
import { institutionalEffectivenessEnabled } from "../../../../utils/constants";
import { Card } from "../../../Card";
import RichTextEditor, {
  rteHasContent,
} from "../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../RichTextArea/RichTextViewer";

const QualitativePreview = ({ measure, key1, key2 }) => {
  const { t } = useTranslation();
  const [structuredQuestions, setStructuredQuestions] = useState(null);

  useEffect(() => {
    // If the measure already carries structured_questions (e.g. from template editing),
    // use them directly and skip the fetch.
    if (
      Array.isArray(measure.structured_questions) &&
      measure.structured_questions.length > 0
    ) {
      setStructuredQuestions(measure.structured_questions);
      return;
    }

    if (!institutionalEffectivenessEnabled || !measure.ddl_id) {
      setStructuredQuestions([]);
      return;
    }

    const fetchQuestions = async () => {
      try {
        const questions =
          await StructuredQuestionService.getStructuredQuestions(
            measure.ddl_id
          );
        setStructuredQuestions(questions);
      } catch {
        setStructuredQuestions([]);
      }
    };

    fetchQuestions();
  }, [measure, institutionalEffectivenessEnabled]);

  const isLoading = structuredQuestions === null;
  const hasStructuredQuestions =
    Array.isArray(structuredQuestions) && structuredQuestions.length > 0;

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="100%">
        <Heading level="h3">
          {measure.name ?? t("assessment.qualitativeMeasure")}
        </Heading>
      </Flex.Item>
      {(rteHasContent(measure.instructions) || measure.description) && (
        <Flex.Item size="100%" padding="0 0 0 large">
          <View
            as="div"
            borderWidth="none none small none"
            padding="0 0 x-small 0"
          >
            <RichTextViewer
              value={measure.instructions || measure.description}
              ariaLabel={t("assessment.instructions")}
            />
          </View>
        </Flex.Item>
      )}
      <Flex.Item size="100%" padding="0 0 0 large">
        {isLoading ? (
          <Skeleton height="8rem" count={2} style={{ marginBottom: 10 }} />
        ) : hasStructuredQuestions ? (
          <Card
            style={{
              backgroundColor: "#F2F8FA",
              border: "1px solid #ccc",
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
            }}
          >
            {structuredQuestions.map((question, index) => (
              <div
                key={question.id || index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <Text weight="bold" size="medium">
                  {question.question_text}
                </Text>
                {rteHasContent(question.helper_text) && (
                  <RichTextViewer
                    value={question.helper_text}
                    ariaLabel={t("structuredQuestions.helperTextFor", {
                      number: index + 1,
                    })}
                  />
                )}
                <View
                  as="div"
                  borderWidth={
                    index === structuredQuestions.length - 1
                      ? undefined
                      : "0 0 small 0"
                  }
                  padding={
                    index === structuredQuestions.length - 1
                      ? undefined
                      : "0 0 small 0"
                  }
                >
                  <RichTextEditor
                    id={`preview-question-${key1}-${key2}-${index}`}
                    label={
                      <Text weight="bold">{t("results.measureResults")}</Text>
                    }
                    defaultValue={t("results.measurePreviewHtml")}
                    uneditable
                  />
                </View>
              </div>
            ))}
          </Card>
        ) : (
          <Card
            style={{ backgroundColor: "#F2F8FA", border: "1px solid #ccc" }}
          >
            <RichTextEditor
              id={`preview-measure-${key1}-${key2}`}
              label={<Text weight="bold">{t("results.measureResults")}</Text>}
              defaultValue={t("results.measurePreviewHtml")}
              uneditable
            />
          </Card>
        )}
      </Flex.Item>
    </Flex>
  );
};

export default QualitativePreview;
